'use client';

import React, { useMemo, useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  ProfileCardHeader,
  AddToListModal,
  Typography,
  Checkbox,
} from 'src/components';
import { CardHeaderContainer, CardLoadingIndicator } from '../Card';
import { faList, faTrophyStar } from '@fortawesome/pro-solid-svg-icons';
import useGetNewTopProviders from 'src/hooks/profiles/useGetNewTopProviders';
import useProfileSelections from 'src/hooks/profiles/useProfileSelections';
import { CardLocationRow } from '../Card/CardLocationRow';
import { InvalidSearchCriteria, NoDataAvailable } from '../NoData';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useLogging } from 'src/context/logging';
import { getEntityIdentifiers } from 'src/utils/getEntityIdentifiers';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';

const NewProfileTopProviders = ({ location = 'hcp' }: { location: string }) => {
  const { entityDetails } = useProfile();
  const { providerId } = getEntityIdentifiers(entityDetails);
  const { selectedFilterSet, selectedCohort } = useProfileSelections(location);
  const log = useLogging();

  // Bulk selection state
  const [selectedProviders, setSelectedProviders] = useState<Set<string>>(
    new Set()
  );
  const [showAddToListModal, setShowAddToListModal] = useState(false);

  const { data: topProvidersData, isLoading } = useGetNewTopProviders({
    providerId,
    location,
  });

  const sortedProviders = useMemo(() => {
    if (!topProvidersData) return [];
    return topProvidersData.results?.slice(0, 5);
  }, [topProvidersData]);

  // check if the saved search has valid procedure codes
  // DRUG codes are excluded as they don't work with Volume By Location (VBL) endpoints
  const isValidSavedSearch = useMemo(() => {
    if (!selectedFilterSet) return true;
    const codeMatch = [
      'cpt',
      'hcpcs',
      'icd',
      'icd10d',
      'icd10p',
      'icdp',
      'drg',
    ];

    return selectedFilterSet?.filters?.some((item) =>
      codeMatch.includes(item.type?.toLowerCase())
    );
  }, [selectedFilterSet]);

  const currentSavedSearch = useMemo(() => {
    if (!selectedFilterSet && !selectedCohort) return '- All Claims';
    return `- ${selectedFilterSet?.label || selectedCohort?.label} Claims`;
  }, [selectedCohort, selectedFilterSet]);

  // Bulk selection handlers
  const handleProviderSelection = useCallback(
    (providerId: string, selected: boolean) => {
      setSelectedProviders((prev) => {
        const newSet = new Set(prev);
        if (selected) {
          newSet.add(providerId);
        } else {
          newSet.delete(providerId);
        }
        return newSet;
      });
    },
    []
  );

  const handleSelectAll = useCallback(() => {
    if (selectedProviders.size === sortedProviders.length) {
      setSelectedProviders(new Set());
    } else {
      setSelectedProviders(
        new Set(sortedProviders.map((p: any) => p.provider_id))
      );
    }
  }, [selectedProviders.size, sortedProviders]);

  const handleBulkAddToList = useCallback(() => {
    setShowAddToListModal(true);
  }, []);

  const handleAddToListSuccess = useCallback(
    (result: { addedCount: number }) => {
      log.event('addProvider', {
        source: 'ProfileTopProviders',
        count: result.addedCount,
        type: 'bulk',
      });
      // Clear selections after successful add
      setSelectedProviders(new Set());
    },
    [log]
  );

  // Get selected providers data for the modal
  const selectedProvidersData = useMemo(
    () =>
      sortedProviders.filter((provider: any) =>
        selectedProviders.has(provider.provider_id)
      ),
    [sortedProviders, selectedProviders]
  );

  const hasSelectedProviders = selectedProviders.size > 0;
  const allSelected =
    selectedProviders.size === sortedProviders.length &&
    sortedProviders.length > 0;

  return (
    <Card className="p-6 bg-white flex-col">
      <CardHeaderContainer>
        <ProfileCardHeader
          icon={faTrophyStar}
          title="Top Providers"
          currentSavedSearch={currentSavedSearch}
          hasSelectedSavedSearch={!!selectedCohort || !!selectedFilterSet}
          subHeader="Procedure* volumes at this location, by claim, 2025"
        />
      </CardHeaderContainer>

      {/* Bulk Selection Header */}
      {sortedProviders?.length > 0 && isValidSavedSearch && (
        <div
          className="flex items-center justify-between p-3 bg-teal-50 border border-teal-200 rounded-lg"
          data-testid="bulk-selection-header"
        >
          <div className="flex items-center gap-3">
            <Checkbox
              checked={allSelected}
              onChange={handleSelectAll}
              size="sm"
              variant="solid"
              color="primary"
              className="flex-shrink-0"
              data-testid="bulk-select-all-checkbox"
            />
            <span
              className="text-sm font-medium text-teal-900"
              data-testid="bulk-select-all-label"
            >
              Select all
            </span>
          </div>

          <button
            onClick={handleBulkAddToList}
            disabled={!hasSelectedProviders}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-teal-600
                     hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500
                     rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="bulk-add-to-list-button"
          >
            <Icon icon={faList} className="w-3 h-3" />
            Add to List
          </button>
        </div>
      )}
      <CardContent className="relative min-h-[5rem]">
        {/* Loading overlay */}
        {isLoading && <CardLoadingIndicator />}

        {/* Main content */}
        <Box
          className={`flex flex-col items-start gap-2 ${
            sortedProviders?.length > 0 && isValidSavedSearch ? 'pt-4' : ''
          }`}
        >
          {!isValidSavedSearch ? (
            <InvalidSearchCriteria />
          ) : sortedProviders?.length > 0 ? (
            sortedProviders.map((provider: any, index: number) => (
              <CardLocationRow
                key={index}
                location={provider}
                isSelected={selectedProviders.has(provider.provider_id)}
                onSelectionChange={(selected) =>
                  handleProviderSelection(provider.provider_id, selected)
                }
                showSelection={true}
                data-testid={`provider-row-${provider.provider_id}`}
              />
            ))
          ) : (
            !isLoading && <NoDataAvailable />
          )}
        </Box>
      </CardContent>
      <Box className="pt-4">
        <Typography variant="caption" className="text-[#555E68] font-normal">
          Prescription data not available in this widget.
        </Typography>
      </Box>

      {/* Bulk Add Modal */}
      <AddToListModal
        isOpen={showAddToListModal}
        onClose={() => setShowAddToListModal(false)}
        mode="add"
        providers={selectedProvidersData}
        onSuccess={handleAddToListSuccess}
      />
    </Card>
  );
};

export default NewProfileTopProviders;
